import React, { useEffect, useMemo, useRef, useState } from 'react';
import { buildOverlapFixInstruction, describeOverlapTarget, planClassOverlap, SEVERITY_NOTE } from '../../../utils/classOverlapFix';
import { trackSaenggibuUxEvent } from '../../../utils/eventTracker';
import './ClassCheckPanel.css';

const VISIBLE_STUDENTS = 5;

/**
 * 일괄 결과 위 반 점검표. 기준은 utils/sentenceOverlap.js (같은 문장 / 거의 같은 문장, 몇 명인지).
 * 묶음마다 앞 번호 학생은 그대로 두고 나머지만 고친다. 한 번에 고치기가 기본, 한 명씩도 가능.
 */
const ClassCheckPanel = ({
  results = [],
  plan: planProp = null,
  contextText = '',
  category = '',
  kinds = {},
  busyIndex = null,
  fixProgress = null,
  disabled = false,
  onMakeDifferent,
  onFixAll,
}) => {
  const [showAll, setShowAll] = useState(false);
  const texts = useMemo(() => results.map((result) => result?.generated_content || result?.content || ''), [results]);
  const names = useMemo(() => results.map((result) => result?.student_name || ''), [results]);
  const plan = useMemo(
    () => planProp || planClassOverlap(texts, { contextText, category }),
    [category, contextText, planProp, texts],
  );
  const usable = texts.filter((text) => text.trim()).length;
  const shownKeyRef = useRef('');

  const { summary, targets } = plan;
  useEffect(() => {
    if (usable < 2) return;
    const key = `${summary.studentCount}:${summary.flaggedStudents}:${summary.sameGroups}:${summary.nearGroups}`;
    if (shownKeyRef.current === key) return;
    shownKeyRef.current = key;
    trackSaenggibuUxEvent('saenggibu_class_check_shown', {
      saenggibu_category: category,
      is_batch: true,
      student_count: summary.studentCount,
      flagged_count: summary.flaggedStudents,
      same_count: summary.sameGroups,
      near_count: summary.nearGroups,
      max_people: summary.maxPeople,
      level: summary.severity,
      opening_count: plan.aux.openingCount,
      slop_count: plan.aux.slopCount,
    });
  }, [category, plan.aux.openingCount, plan.aux.slopCount, summary, usable]);

  if (usable < 2) return null;

  const busy = disabled || busyIndex !== null || Boolean(fixProgress);
  const instructionFor = (target) => buildOverlapFixInstruction(target, { text: texts[target.index], kinds, category });

  if (targets.length === 0) {
    return (
      <div className="class-check is-clean" data-testid="class-check">
        <strong>학생마다 다른 문장으로 써졌어요</strong>
        <span>반 전체에서 같은 문장·거의 같은 문장이 없어요.</span>
      </div>
    );
  }

  const groupCount = summary.sameGroups + summary.nearGroups;
  const visible = showAll ? targets : targets.slice(0, VISIBLE_STUDENTS);
  const note = SEVERITY_NOTE[summary.severity];

  return (
    <section className="class-check" data-testid="class-check">
      <div className="class-check-head">
        <div className="class-check-title">
          <strong>다른 학생과 겹치는 문장 {groupCount}곳</strong>
          <span>
            {summary.sameGroups > 0 && `같은 문장 ${summary.sameGroups}`}
            {summary.sameGroups > 0 && summary.nearGroups > 0 && ' · '}
            {summary.nearGroups > 0 && `거의 같은 문장 ${summary.nearGroups}`}
          </span>
        </div>
      </div>
      {note && <p className="class-check-note" data-testid="class-check-severity">{note}</p>}

      <button
        type="button"
        className="class-check-fix-all"
        data-testid="class-check-fix-all"
        disabled={busy}
        onClick={() => onFixAll?.(targets.map((target) => ({ index: target.index, instruction: instructionFor(target) })), summary)}
      >
        {fixProgress
          ? `고치는 중 ${fixProgress.done}/${fixProgress.total}`
          : `겹치지 않게 ${targets.length}명 고치기`}
      </button>
      <p className="class-check-sub">겹치는 문장마다 한 명은 그대로 두고, 나머지 학생 문장만 바꿔요.</p>

      <ul className="class-check-list">
        {visible.map((target) => (
          <li key={target.index}>
            <div className="class-check-student">
              <span className="class-check-name">
                {names[target.index] || `${target.index + 1}번 학생`}
                {kinds[target.sentences[0].clusterId] === 'activity' && <em className="class-check-tag">공통 활동</em>}
              </span>
              <span className="class-check-reason">{describeOverlapTarget(target)}</span>
            </div>
            <button
              type="button"
              data-testid={`class-check-fix-${target.index}`}
              disabled={busy}
              onClick={() => onMakeDifferent?.(target.index, instructionFor(target), [
                target.sentences.some((s) => s.tier === 'same') ? 'same' : 'near',
              ])}
            >
              {busyIndex === target.index ? '고치는 중' : '고치기'}
            </button>
          </li>
        ))}
      </ul>
      {targets.length > VISIBLE_STUDENTS && (
        <button type="button" className="class-check-more" onClick={() => setShowAll((value) => !value)}>
          {showAll ? '접기' : `${targets.length - VISIBLE_STUDENTS}명 더 보기`}
        </button>
      )}
      {(plan.aux.openingCount > 0 || plan.aux.slopCount > 0) && (
        <p className="class-check-aux">
          참고 · {[
            plan.aux.openingCount > 0 && `첫머리가 같은 학생 ${plan.aux.openingCount}명`,
            plan.aux.slopCount > 0 && `상투어가 많은 학생 ${plan.aux.slopCount}명`,
          ].filter(Boolean).join(' · ')}
        </p>
      )}
    </section>
  );
};

export default ClassCheckPanel;
